/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { BOARD_SOURCE_FIELDS_JQLTERM, BOARD_SOURCE_FIELDS } from './boardTypes.constants.tsx';
import type { GenericPickerOption, JQLTermType } from './BoardCreateFormValues.types.tsx';
import { fireBoardCreateErrorAnalytics } from './fireBoardCreateErrorAnalytics.tsx';
import { useJQLBuilderFetch } from './useJQLBuilderFetch.tsx';
import { useBoardCreateContext } from './useBoardCreateContext.tsx';
import { useBoardCreateFlag } from './useBoardCreateFlag.tsx';
import type { ErrorMessageMap } from './ErrorMessageMap.types.tsx';

const failedToLoadIssueTypesTitle = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-issue-types-title',
	defaultMessage: 'We can’t load issue types right now',
	description: 'Error title when issue types list fails to load',
});

const failedToLoadIssueTypesDescription = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-issue-types-description',
	defaultMessage: 'Refresh the page and try again.',
	description: 'Error description when issue types list fails to load',
});

const failedToLoadLabelsTitle = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-labels-title',
	defaultMessage: 'We can’t load labels right now',
	description: 'Error title when labels list fails to load',
});

const failedToLoadLabelsDescription = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-labels-description',
	defaultMessage: 'Refresh the page and try again.',
	description: 'Error description when labels list fails to load',
});

const failedToLoadOptionsTitle = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-options-title',
	defaultMessage: 'Something went wrong',
	description: 'Error title when the options list fails to load',
});

const failedToLoadIssueTypesTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.controllers.use-load-options-controller.failed-to-load-issue-types-title-issue-terminology-refresh-m4',
	defaultMessage: 'We can’t load work types right now',
	description: 'Error title when issue types list fails to load',
});

const errorMessageMapOld: ErrorMessageMap = {
	[BOARD_SOURCE_FIELDS_JQLTERM[BOARD_SOURCE_FIELDS.ISSUE_TYPES]]: {
		title: failedToLoadIssueTypesTitle,
		description: failedToLoadIssueTypesDescription,
	},
	[BOARD_SOURCE_FIELDS_JQLTERM[BOARD_SOURCE_FIELDS.LABELS]]: {
		title: failedToLoadLabelsTitle,
		description: failedToLoadLabelsDescription,
	},
	DEFAULT: {
		title: failedToLoadOptionsTitle,
		description: failedToLoadOptionsTitle,
	},
};

const errorMessageMap: ErrorMessageMap = {
	[BOARD_SOURCE_FIELDS_JQLTERM[BOARD_SOURCE_FIELDS.ISSUE_TYPES]]: {
		title: failedToLoadIssueTypesTitleIssueTerminologyRefreshM4,
		description: failedToLoadIssueTypesDescription,
	},
	[BOARD_SOURCE_FIELDS_JQLTERM[BOARD_SOURCE_FIELDS.LABELS]]: {
		title: failedToLoadLabelsTitle,
		description: failedToLoadLabelsDescription,
	},
	DEFAULT: {
		title: failedToLoadOptionsTitle,
		description: failedToLoadOptionsTitle,
	},
};

export const useIsLoading = (): [boolean, Dispatch<SetStateAction<boolean>>] => useState(false);

export const useLoadOptionsController = (): {
	loadOptions: (
		jqlTerm: JQLTermType,
	) => (searchString: string) => Promise<GenericPickerOption[] | undefined>;
	isLoading: boolean;
} => {
	const { projectId } = useBoardCreateContext();
	const jqlBuilderFetch = useJQLBuilderFetch({ projectId });
	const [isLoading, setIsLoading] = useIsLoading();
	const { showErrorFlag } = useBoardCreateFlag();
	const { formatMessage } = useIntl();

	const loadOptions: (
		jqlTerm: JQLTermType,
	) => (searchString: string) => Promise<GenericPickerOption[] | undefined> = useCallback(
		(jqlTerm: JQLTermType) => async (searchString: string) => {
			setIsLoading(true);
			const errorMessages = fg('jira-issue-terminology-update-m4')
				? errorMessageMap
				: errorMessageMapOld;
			return jqlBuilderFetch(jqlTerm, searchString)
				.catch((error) => {
					const errorText = errorMessages[jqlTerm] || errorMessages.DEFAULT;
					showErrorFlag({
						title: formatMessage(errorText.title),
						description: formatMessage(errorText.description),
					});
					fireBoardCreateErrorAnalytics({
						error: new Error(`Failed to fetch picker options: ${error}`),
						attributes: {
							jqlTerm,
						},
						id: 'fetchPickerOptions',
					});
					return [];
				})
				.finally(() => {
					setIsLoading(false);
				});
		},
		[formatMessage, jqlBuilderFetch, setIsLoading, showErrorFlag],
	);

	return {
		loadOptions,
		isLoading,
	};
};
